import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import VehicleDueAlert from "@/components/VehicleDueAlert";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wallet, ShoppingCart, RefreshCw, Users, Building2, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend, LabelList,
} from "recharts";

const fmtShort = (n) => {
  n = Number(n || 0);
  const c = (v) => v.toFixed(1).replace(".", ",").replace(",0", "");
  if (n >= 1e9) return c(n / 1e9) + "M";
  if (n >= 1e6) return c(n / 1e6) + "jt";
  if (n >= 1e3) return (n / 1e3).toFixed(0) + "rb";
  return String(n);
};
const LBL = { fontSize: 10, fontWeight: 700, fill: "#0f172a" };

const ChartCard = ({ title, hint, empty, children, testid }) => (
  <Card className="p-4 sm:p-5" data-testid={testid}>
    <div className="mb-3 flex items-baseline justify-between gap-2">
      <h3 className="text-sm sm:text-base font-semibold text-slate-800">{title}</h3>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
    {empty ? (
      <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">Belum ada data.</div>
    ) : (
      <ResponsiveContainer width="100%" height={240}>{children}</ResponsiveContainer>
    )}
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const dateAxis = isMobile
    ? { tick: { fontSize: 9 }, interval: 0, angle: -60, textAnchor: "end", height: 40 }
    : { tick: { fontSize: 11 }, interval: 0 };

  const buildQ = () => {
    const p = new URLSearchParams();
    if (projectId !== "all") p.set("project_id", projectId);
    return p.toString();
  };
  const load = () => api.get(`/dashboard?${buildQ()}`).then((r) => setData(r.data));

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await Promise.all([load(), api.get("/projects").then((r) => setProjects(r.data))]);
      toast.success("Data dashboard diperbarui");
    } catch (e) { toast.error("Gagal memperbarui data"); }
    setRefreshing(false);
  };

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [projectId]);

  const g = data?.beban_gaji || {};
  const b = data?.beban_belanja || {};
  const gajiProyek = data?.chart_gaji_proyek || [];
  const karyawanProyek = data?.chart_karyawan_proyek || [];
  const kategori = data?.chart_belanja_kategori || [];
  const belanjaProyek = data?.belanja_proyek || [];
  const absensiHarian = data?.chart_absensi_harian || [];
  const bb = data?.beban_gaji_akumulasi || {};
  const fmtDate = (s) => (s ? s.split("-").reverse().join("/") : "-");

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" subtitle="Ringkasan biaya harian & per proyek">
        <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing} data-testid="dash-refresh-btn">
          <RefreshCw className={`h-4 w-4 sm:mr-1 ${refreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">{refreshing ? "Memuat..." : "Refresh"}</span>
        </Button>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-[150px] sm:w-[180px]" data-testid="dash-project-filter"><SelectValue placeholder="Proyek" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Proyek</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      <VehicleDueAlert />

      {/* 1 & 2: Beban Gaji & Belanja per Hari (otomatis update) */}
      <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
        <Card className="relative overflow-hidden p-5 bg-blue-900 text-white" data-testid="card-beban-gaji">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-xs font-medium text-blue-200">Beban Gaji per Hari (termasuk Tunjangan & UM)</div>
              <div className="mt-1 text-2xl sm:text-3xl font-black font-mono tabular truncate" data-testid="gaji-harian">{rupiah(g.harian)}</div>
              <div className="mt-1 text-xs text-blue-200">Anggaran {g.jumlah_karyawan ?? 0} karyawan aktif / hari</div>
            </div>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-700"><Wallet className="h-6 w-6" /></div>
          </div>
          <div className="mt-4 rounded-lg bg-blue-800/60 px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-blue-200">Realisasi hari ini ({g.hadir_hari_ini ?? 0} hadir)</span>
            <span className="text-base font-bold font-mono" data-testid="gaji-hari-ini">{rupiah(g.hari_ini)}</span>
          </div>
        </Card>

        <Card className="relative overflow-hidden p-5 bg-rose-700 text-white" data-testid="card-beban-belanja">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-xs font-medium text-rose-100">Beban Belanja Hari Ini</div>
              <div className="mt-1 text-2xl sm:text-3xl font-black font-mono tabular truncate" data-testid="belanja-hari-ini">{rupiah(b.hari_ini)}</div>
              <div className="mt-1 text-xs text-rose-100">Rata-rata {rupiah(b.rata_harian)} / hari belanja</div>
            </div>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-600"><ShoppingCart className="h-6 w-6" /></div>
          </div>
          <div className="mt-4 rounded-lg bg-rose-800/60 px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-rose-100">Total belanja {projectId !== "all" ? "(proyek ini)" : "keseluruhan"}</span>
            <span className="text-base font-bold font-mono" data-testid="belanja-total">{rupiah(b.total)}</span>
          </div>
        </Card>
      </div>

      {/* Beban Gaji Terakumulasi BELUM DIBAYAR (otomatis sejak periode terbayar terakhir) */}
      <Card className="relative overflow-hidden p-5 border-2 border-amber-400 bg-amber-50" data-testid="card-beban-akumulasi">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-700">
              <AlertTriangle className="h-4 w-4" /> Beban Gaji Terakumulasi Belum Dibayar (harus disiapkan)
            </div>
            <div className="mt-1 text-3xl sm:text-4xl font-black font-mono tabular text-amber-900 truncate" data-testid="beban-akumulasi-total">{rupiah(bb.total)}</div>
            <div className="mt-1 text-xs text-amber-700">
              {bb.sejak ? `Sejak ${fmtDate(bb.sejak)}` : "Sejak awal data"} s/d {fmtDate(bb.sampai)} · {Math.round((bb.hari_orang || 0) * 10) / 10} hari-orang
              {bb.paid_through ? ` · terbayar s/d ${fmtDate(bb.paid_through)}` : ""} · otomatis
            </div>
          </div>
          <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-amber-900"><Wallet className="h-7 w-7" /></div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {[
            { k: "gaji_pokok", l: "Gaji Pokok" },
            { k: "uang_makan", l: "Uang Makan" },
            { k: "uang_luar_kota", l: "Uang Luar Kota" },
            { k: "uang_lembur", l: "Lembur" },
            { k: "tunjangan", l: "Tunjangan" },
          ].map((it) => (
            <div key={it.k} className="rounded-lg border border-amber-200 bg-white px-3 py-2" data-testid={`beban-akumulasi-${it.k}`}>
              <div className="text-[11px] text-slate-500">{it.l}</div>
              <div className="font-mono text-sm font-bold text-slate-800">{rupiah(bb[it.k])}</div>
            </div>
          ))}
        </div>
        {(bb.per_hari || []).length > 0 && (
          <div className="mt-4">
            <div className="mb-2 text-xs font-semibold text-amber-700">Beban Gaji per Tanggal (akumulasi {rupiah(bb.total)})</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={bb.per_hari} margin={{ top: isMobile ? 34 : 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#fde68a" />
                <XAxis dataKey="tanggal" {...dateAxis} />
                <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={44} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Bar dataKey="total" fill="#d97706" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="total" position="top" formatter={(v) => (v ? fmtShort(v) : "")}
                    {...(isMobile ? { angle: -90, textAnchor: "start", dx: 3, style: { ...LBL, fontSize: 8 } } : { style: LBL })} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Target Belanja Harian per Proyek (merah bila lewat anggaran) */}
      {belanjaProyek.length > 0 && (
        <Card className="p-4 sm:p-5" data-testid="card-belanja-target">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h3 className="text-sm sm:text-base font-semibold text-slate-800">Belanja Hari Ini vs Target per Proyek</h3>
            <span className="text-[11px] text-slate-400">merah = lewat anggaran</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {belanjaProyek.map((p, i) => (
              <div key={i} data-testid={`belanja-target-${i}`}
                className={`rounded-xl border p-3 ${p.over ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-800">{p.project}</span>
                  {p.over && <span className="shrink-0 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">LEWAT TARGET</span>}
                </div>
                <div className="mt-1 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] text-slate-500">Belanja hari ini</div>
                    <div className={`font-mono text-base font-bold ${p.over ? "text-rose-700" : "text-slate-900"}`}>{rupiah(p.belanja_hari_ini)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-slate-500">Target / hari</div>
                    <div className="font-mono text-sm text-slate-600">{p.anggaran > 0 ? rupiah(p.anggaran) : "—"}</div>
                  </div>
                </div>
                {p.anggaran > 0 && (
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className={`h-full rounded-full ${p.over ? "bg-rose-600" : "bg-emerald-500"}`}
                      style={{ width: `${Math.min(100, (p.belanja_hari_ini / p.anggaran) * 100)}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Jumlah Karyawan Masuk per Hari (14 hari terakhir) */}
      <ChartCard title="Jumlah Karyawan Masuk per Hari" hint="14 hari terakhir" empty={absensiHarian.length === 0} testid="chart-absensi-harian">
        <BarChart data={absensiHarian} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="tanggal" {...dateAxis} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
          <Tooltip formatter={(v) => [`${v} orang`, "Masuk"]} />
          <Bar dataKey="jumlah" fill="#0d9488" radius={[5, 5, 0, 0]}>
            <LabelList dataKey="jumlah" position="top" style={{ fontSize: 11, fontWeight: 700, fill: "#0f172a" }} />
          </Bar>
        </BarChart>
      </ChartCard>

      {/* 4: Head to Head per Proyek - Beban Gaji (stacked) */}
      <ChartCard title="Head to Head Beban Gaji per Proyek" hint="per hari" empty={gajiProyek.length === 0} testid="chart-gaji-proyek">
        <BarChart data={gajiProyek.map((x) => ({ ...x, _tot: (x.gaji_pokok || 0) + (x.um || 0) + (x.tunjangan || 0) }))} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="project" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={48} />
          <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={44} />
          <Tooltip formatter={(v, n) => [rupiah(v), n]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="gaji_pokok" name="Gaji Pokok" stackId="a" fill="#1e3a8a" radius={[0, 0, 0, 0]} />
          <Bar dataKey="um" name="Uang Makan/Luar Kota" stackId="a" fill="#f59e0b" />
          <Bar dataKey="tunjangan" name="Tunjangan" stackId="a" fill="#7c3aed" radius={[5, 5, 0, 0]}>
            <LabelList dataKey="_tot" position="top" formatter={(v) => (v ? fmtShort(v) : "")} style={LBL} />
          </Bar>
        </BarChart>
      </ChartCard>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        {/* 3: Jumlah Karyawan per Proyek */}
        <ChartCard title="Jumlah Karyawan per Proyek" empty={karyawanProyek.length === 0} testid="chart-karyawan-proyek">
          <BarChart data={karyawanProyek} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="project" width={96} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [`${v} orang`, "Karyawan"]} />
            <Bar dataKey="jumlah" fill="#0d9488" radius={[0, 6, 6, 0]}>
              {karyawanProyek.map((e, i) => (
                <Cell key={i} fill={["#0d9488", "#1e3a8a", "#f59e0b", "#7c3aed", "#e11d48"][i % 5]} />
              ))}
            </Bar>
          </BarChart>
        </ChartCard>

        {/* 5: Kategori Belanja per Proyek (ikut filter proyek global) */}
        <ChartCard title="Kategori Belanja per Proyek" hint={projectId !== "all" ? "proyek terpilih" : "semua proyek"} empty={kategori.length === 0} testid="chart-belanja-kategori">
          <BarChart data={kategori} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="kategori" width={96} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => rupiah(v)} />
            <Bar dataKey="total" radius={[0, 6, 6, 0]}>
              {kategori.map((e, i) => (
                <Cell key={i} fill={["#1e3a8a", "#f97316", "#7c3aed", "#0d9488", "#e11d48", "#f59e0b"][i % 6]} />
              ))}
            </Bar>
          </BarChart>
        </ChartCard>
      </div>

      <div className="grid grid-cols-2 gap-3 md:hidden">
        <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-700" />
          <div><div className="text-[11px] text-slate-500">Karyawan</div><div className="text-sm font-bold text-slate-800">{g.jumlah_karyawan ?? 0}</div></div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-teal-700" />
          <div><div className="text-[11px] text-slate-500">Proyek</div><div className="text-sm font-bold text-slate-800">{karyawanProyek.length}</div></div>
        </div>
      </div>
    </div>
  );
}
